import { apiClient } from './client'

export interface PoolInfo {
  active: number
}

export interface WorkerStats {
  active: number
  pools: {
    pipeline: PoolInfo
    creation: PoolInfo
    other: PoolInfo
  }
}

export interface QueueStats {
  total: number
  by_status: Record<string, number>
  worker: WorkerStats
}

export interface MonitorPipeline {
  id: string
  pipeline_type: string
  title: string
  status: string
  progress: number
  current_step: string | null
  step_index: number | null
  total_steps: number | null
  error_message: string | null
  created_at: string
  updated_at: string
  started_at: string | null
  step_started_at: string | null
  progress_config?: Array<{ step: string; index: number; progress: number; duration_sec?: number }>
}

export interface MonitorCreationTask {
  id: string
  module_type: string
  creation_type: string
  title: string
  status: string
  progress: number
  expected_output_count: number
  succeeded_output_count: number
  error_code: string | null
  error_message: string | null
  model_name: string
  provider: string
  created_at: string
  updated_at: string
  started_at: string | null
}

export interface MonitorCommand {
  id: string
  name: string
  app: string
  status: string
  args: Record<string, unknown>
  error_message: string | null
  created_at: string
  updated_at: string
  started_at: string | null
  completed_at: string | null
  attempts: number
}

export interface TaskSystemMonitor {
  queue: QueueStats
  pipelines: {
    items: MonitorPipeline[]
    by_status: Record<string, number>
  }
  creation_tasks: {
    items: MonitorCreationTask[]
    by_status: Record<string, number>
  }
  recent_commands: MonitorCommand[]
}

export interface MonitorPipelineChildTask {
  step: string
  creation_task_id: string | null
  command_id: string | null
  status: string
  progress: number
  work_ids: string[]
  error: string | null
}

export interface MonitorPipelineHistoryEvent {
  id: number
  event_type: string
  step_name: string | null
  event_data: MonitorLlmDiagnostics & Record<string, unknown>
  created_at: string
}

/** LLM 调用诊断 —— 流水线 analyze_product 步骤记录到 history event_data 中，
 *  供任务监控展示「用了哪个模型 / 调用参数 / 返回值」。 */
export interface MonitorLlmDiagnostics {
  llm_model?: {
    model?: string
    provider?: string
    base_url?: string
    model_type?: string
    source?: string
  } | null
  llm_params?: {
    temperature?: number
    top_p?: number
    tool?: string
    tool_choice?: string
    image_count?: number
    prompt_len?: number
  } | null
  /** 模型实际返回值摘要 —— 排查「模型未调用输出工具」时直接展示模型返回了什么。 */
  llm_response?: {
    finish_reason?: string | null
    has_tool_calls?: boolean
    no_choices?: boolean
    content?: string
    tool_calls?: Array<{ name?: string; args?: string }>
    raw_preview?: string
  } | null
}

export interface MonitorPipelineDetail {
  id: string
  pipeline_type: string
  title: string
  status: string
  progress: number
  current_step: string | null
  step_index: number | null
  total_steps: number | null
  error_code: string | null
  error_message: string | null
  created_at: string
  updated_at: string
  started_at: string | null
  completed_at: string | null
  result: Record<string, unknown> | null
  input_assets: Array<Record<string, unknown>>
  child_tasks: MonitorPipelineChildTask[]
  creation_tasks: Array<MonitorCreationTask & Record<string, unknown>>
  history: MonitorPipelineHistoryEvent[]
}

export const taskMonitorApi = {
  /** Single source of truth for all task data. */
  getTaskSystemStatus: async () => {
    const response = await apiClient.get<TaskSystemMonitor>(
      '/agent-api/creation/monitor/task-system',
    )
    return response.data
  },
  /** Detailed step + error diagnostics for one pipeline. */
  getPipelineDetail: async (pipelineId: string) => {
    const response = await apiClient.get<MonitorPipelineDetail>(
      `/agent-api/creation/monitor/pipeline/${pipelineId}`,
    )
    return response.data
  },
}